// Auth: username+password (instant, no verification), Google Sign-In, the auth modal, and session state.
// currentUser is shared with the main script via state.js (ES module live bindings) —
// this file is the only place allowed to call setCurrentUser().
//
// This module needs a few things the main script owns (rendering the who-bar,
// the shared modal stack, unread badges). Rather than importing the main inline
// script (which isn't possible — it's not a separate file), the main script hands
// those in once via initAuth(deps) right after it defines them.

import { apiGet, apiPost } from './api.js';
import { currentUser, setCurrentUser } from './state.js';
import { syncPushSubscription, clearPushSubscription } from './push.js';

let _deps = null;
export function initAuth(deps) {
  // deps: { renderWhoBar, closeUIModal, pushUIModal, refreshUnreadBadge, refreshNotifBadge }
  _deps = deps;
}

let pendingAction = null; // fn to re-run once login completes

export async function checkAuth() {
  try {
    const { user } = await apiGet('/api/auth/me');
    setCurrentUser(user);
  } catch (e) { setCurrentUser(null); }
  _deps.renderWhoBar();
  _deps.refreshUnreadBadge();
  _deps.refreshNotifBadge();
  // Re-associates this device's push subscription with whoever's now
  // logged in (or does nothing if push was never enabled here) — this is
  // what makes push work again after e.g. reopening the app days later.
  if (currentUser) syncPushSubscription();
}

export function requireAuth(action) {
  if (currentUser) { action(); return; }
  pendingAction = action;
  openAuthModal();
}

/* ---- Google Sign-In ---- */
let googleClientId = null, gsiReady = false;
// Hands off from the early stub in <head> (see comment there) — covers
// both possible load orders: if Google's script already finished and
// called the stub, __gsiScriptLoaded is already true and we run
// immediately; otherwise the stub will call this the moment it does load.
window.__gsiReadyHandler = function () { gsiReady = true; tryInitGoogle(); };
if (window.__gsiScriptLoaded) window.__gsiReadyHandler();

export async function loadPublicConfig() {
  try {
    const { google_client_id } = await apiGet('/api/config');
    googleClientId = google_client_id;
  } catch (e) { }
  tryInitGoogle();
  // Final safety net: if neither the query-param callback nor the native
  // <script onload> fired within a couple seconds, fall back to polling for
  // the object Google's script actually creates. This covers environments
  // where both onload signals get swallowed (some extensions/proxies do
  // this) but the script still loaded and works fine.
  let pollTries = 0;
  const pollId = setInterval(() => {
    pollTries++;
    if (gsiReady || pollTries > 25) { clearInterval(pollId); return; }
    if (typeof google !== 'undefined' && google.accounts && google.accounts.id) {
      gsiReady = true;
      window.__gsiScriptLoaded = true;
      tryInitGoogle();
      clearInterval(pollId);
    }
  }, 200);
}

export function tryInitGoogle() {
  updateGoogleAvailabilityUI();
  if (!gsiReady || !googleClientId || typeof google === 'undefined') return;
  google.accounts.id.initialize({ client_id: googleClientId, callback: onGoogleCredential });
  renderGoogleButton();
}

// Renders the real Google button into every spot it belongs — the auth modal
// (reached mid-action, e.g. hitting a login wall while posting) AND the
// Profile tab itself, so signing in with Google doesn't require finding a
// modal first.
export function renderGoogleButton() {
  if (!gsiReady || !googleClientId || typeof google === 'undefined') return;
  ['gsiBtn', 'gsiBtnProfile'].forEach(id => {
    const el = document.getElementById(id);
    if (!el) return;
    el.innerHTML = '';
    google.accounts.id.renderButton(el, { theme: 'outline', size: 'large', width: 260 });
  });
  updateGoogleAvailabilityUI();
}

// Google Sign-In depends on /api/config returning a GOOGLE_CLIENT_ID, which
// only happens once this is actually running behind Cloudflare Pages
// Functions (not just opened as a static HTML file, and not before that
// request resolves). Until it's confirmed available, hide the button slot
// AND its "or with email" divider entirely — a good-looking empty gap where
// a button should be is exactly the kind of thing that reads as broken/
// unprofessional, so we collapse it instead of leaving dead space.
export function updateGoogleAvailabilityUI() {
  const available = !!(gsiReady && googleClientId && typeof google !== 'undefined');
  [['gsiBtn', 'gsiDividerAuth'], ['gsiBtnProfile', 'gsiDividerProfile']].forEach(([btnId, divId]) => {
    const btn = document.getElementById(btnId);
    const div = document.getElementById(divId);
    if (btn) btn.style.display = available ? 'flex' : 'none';
    if (div) div.style.display = available ? '' : 'none';
  });
}

// Google's own rendered button visibly does nothing for a moment after the
// account picker closes — the credential still has to make a round trip to
// our own /api/auth/google before anything on screen changes, and with no
// feedback in that window it reads as stuck/frozen rather than working.
// Swaps Google's button out for a small spinner for that window;
// renderGoogleButton() (called right after, success or fail) puts Google's
// real button back — same container, same el.innerHTML='' + renderButton()
// it already does on every other (re)render.
function showGoogleSigningIn() {
  ['gsiBtn', 'gsiBtnProfile'].forEach(id => {
    const el = document.getElementById(id);
    if (el) el.innerHTML = `<div class="gsi-loading"><span class="gsi-spinner"></span>Signing in…</div>`;
  });
}
export async function onGoogleCredential(resp) {
  showGoogleSigningIn();
  try {
    const { user } = await apiPost('/api/auth/google', { credential: resp.credential });
    setCurrentUser(user);
    _deps.renderWhoBar();
    closeAuthModal();
    syncPushSubscription();
    if (pendingAction) { const fn = pendingAction; pendingAction = null; fn(); }
  } catch (e) {
    setAuthMsg(e.data && e.data.message ? e.data.message : 'Google sign-in failed.', true);
  } finally {
    renderGoogleButton();
  }
}

/* ---- Username/password modal (instant, no verification) ---- */
let authTab = 'login';

export function openAuthModal() {
  // Google's client script is no longer loaded on every app open (see
  // ensureGSI in index.html) — anything that can show a Google button asks
  // for it here. Idempotent, and a no-op if the boot warm-up already did it.
  if (window.ensureGSI) window.ensureGSI();
  authTab = 'login';
  document.getElementById('authModal').classList.add('active');
  renderAuthModal();
  setTimeout(renderGoogleButton, 0);
  _deps.pushUIModal('auth');
}
export function hideAuthModalUI() { document.getElementById('authModal').classList.remove('active'); pendingAction = null; }
export function closeAuthModal() { _deps.closeUIModal('auth', hideAuthModalUI); }
export function setAuthTab(tab) { authTab = tab; renderAuthModal(); setTimeout(renderGoogleButton, 0); }
function setAuthMsg(msg, isErr) {
  const el = document.getElementById('authMsg');
  if (el) { el.textContent = msg; el.style.color = isErr ? 'var(--rust)' : 'var(--ink-soft)'; }
}
function renderAuthModal() {
  const tabsHtml = `
    <div style="display:flex;gap:8px;justify-content:center;margin-bottom:14px;">
      <span onclick="setAuthTab('login')" style="padding:6px 14px;border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:11.5px;cursor:pointer;${authTab === 'login' ? 'background:var(--forest);color:var(--white)' : 'color:var(--ink-soft)'}">Log in</span>
      <span onclick="setAuthTab('signup')" style="padding:6px 14px;border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:11.5px;cursor:pointer;${authTab === 'signup' ? 'background:var(--forest);color:var(--white)' : 'color:var(--ink-soft)'}">Sign up</span>
    </div>
    <div id="gsiBtn" style="display:flex;justify-content:center;margin-bottom:14px;"></div>
    <div id="gsiDividerAuth" class="divider">or with a username</div>`;

  const formHtml = authTab === 'login' ? `
      <input type="text" id="authUsername" placeholder="Username" autocomplete="username">
      <input type="password" id="authPass" placeholder="Password" autocomplete="current-password">
      <button class="btn-primary" style="margin-top:2px" onclick="doLogin()">Log in</button>`
    : `
      <input type="text" id="authUsername" placeholder="Choose a username" autocomplete="username">
      <input type="password" id="authPass" placeholder="Choose a password (4+ characters)" autocomplete="new-password">
      <button class="btn-primary" style="margin-top:2px" onclick="doSignup()">Create account</button>`;

  document.getElementById('authCardBody').innerHTML = `
    <h3>${authTab === 'login' ? 'Log in' : 'Create your account'}</h3>
    ${tabsHtml}
    ${formHtml}
    <div id="authMsg" style="margin-top:10px;font-size:12px;color:var(--ink-soft);"></div>
    <div class="auth-close" onclick="closeAuthModal()">Not now</div>`;
}

export async function doLogin() {
  const username = document.getElementById('authUsername').value.trim();
  const password = document.getElementById('authPass').value;
  if (!username) { setAuthMsg('Enter your username.', true); return; }
  try {
    const { user } = await apiPost('/api/auth/login', { username, password });
    setCurrentUser(user);
    _deps.renderWhoBar();
    closeAuthModal();
    syncPushSubscription();
    if (pendingAction) { const fn = pendingAction; pendingAction = null; fn(); }
  } catch (e) {
    setAuthMsg((e.data && e.data.message) || "This preview isn't connected to a live backend yet.", true);
  }
}

export async function doSignup() {
  const username = document.getElementById('authUsername').value.trim();
  const password = document.getElementById('authPass').value;
  if (username.length < 3) { setAuthMsg('Username must be at least 3 characters.', true); return; }
  if (password.length < 4) { setAuthMsg('Password must be at least 4 characters.', true); return; }
  try {
    // Signup logs the account in immediately — no verification step.
    const { user } = await apiPost('/api/auth/signup', { username, password });
    setCurrentUser(user);
    _deps.renderWhoBar();
    closeAuthModal();
    syncPushSubscription();
    if (pendingAction) { const fn = pendingAction; pendingAction = null; fn(); }
    // Fresh account just created — show the "suggested accounts" onboarding
    // step so they don't land on an empty Following feed. Never blocks:
    // the sheet is fully skippable (see Task 2).
    if (_deps.showSuggestedFollows) setTimeout(_deps.showSuggestedFollows, 300);
  } catch (e) {
    setAuthMsg((e.data && e.data.message) || "This preview isn't connected to a live backend yet.", true);
  }
}

// The app keeps last-seen copies of a few things in localStorage so screens
// paint instantly on open (see readLocalCache/writeLocalCache in app.js) —
// including the message inbox and recent threads. Those are personal, so
// they leave with the account rather than sitting on the device for whoever
// signs in next. The service worker's own /api/* snapshot is dropped for the
// same reason. Public, non-account data (places, the shared playlist) is left
// alone: it's the same for everyone and re-caching it costs a round trip.
function clearAccountCaches() {
  try {
    const doomed = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k) continue;
      if (k.startsWith('thikana_cache_inbox_') ||
          k.startsWith('thikana_cache_threads_') ||
          k.startsWith('thikana_cache_feed_')) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* private mode / disabled storage — nothing to clear */ }
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'thikana-clear-api-cache' });
  } catch (e) { }
}

export async function logout() {
  try { await apiPost('/api/auth/logout'); } catch (e) { }
  await clearPushSubscription(); // stop this device getting pushes for the account that's leaving
  clearAccountCaches();
  setCurrentUser(null);
  _deps.renderWhoBar();
}
